// lib/clients/syfak.ts
// Client configuration for syfak (syfak.gr / syfakb2b.gr) — Greek pharmacy B2B
// wholesale. Architecturally an acalight twin: B2B, OpenCart, login-gated
// per-customer pricing, Greek-only.
//
// PHASE 1 = catalogue + search + facets demo, plus the AI ordering assistant
// (agentChat, enabled 2026-06-12). No per-customer pricing, no add-to-cart.
// The B2B plumbing (externalPricing hook, cart dispatcher, CORS) is the same
// as acalight's and is wired but flag-OFF, so Phase 2 is a config flip — see
// the `// Phase 2:` markers below.
//
// ⚠️ PLACEHOLDER values (brand color, example queries) need real input from
// syfak before staging.

import type { ClientConfig } from './types';

// ── Product Categories ──
// The 7 EXACT values of the `category` facet in products_syfak (level-1 of the
// feed's "Κατηγορία / Υποκατηγορία" taxonomy). resolveCategories matches the
// agent's picks against this facet, so only these names scope correctly. They
// are COARSE (74% of the catalogue is Παραφάρμακα) — the prompt tells the
// agent to use them sparingly and narrow via searchTerm/keywords/filters.
const PRODUCT_CATEGORIES: string[] = [
  'Παραφάρμακα',
  'Φάρμακα Συντ/μενα',
  'Φάρμακα μη συντ/μενα',
  'Γάλατα',
  'Φάρμακα Αρνητική Λίστα',
  'ΦΑΡΜΑΚΑ ΥΨΗΛΟΥ ΚΟΣΤΟΥΣ',
  'Διάφορα',
];

// ── Product Concepts ──
// Flexible keyword vocabulary mirroring the real catalogue (158 product
// groups): OTC, supplements, dermocosmetics, oral care, baby, devices,
// wound care, pharmacy consumables. Greek + Latin as found in titles.
const PRODUCT_CONCEPTS: string[] = [
  // OTC / φάρμακα
  'παυσίπονο', 'αντιπυρετικό', 'paracetamol', 'depon', 'ibuprofen', 'ασπιρίνη',
  'σιρόπι βήχα', 'αποσυμφορητικό', 'αντιισταμινικό', 'αλλεργία', 'αντιόξινο',
  // Συμπληρώματα διατροφής
  'βιταμίνη c', 'βιταμίνη d', 'μαγνήσιο', 'σίδηρος', 'πολυβιταμίνη',
  'προβιοτικά', 'ωμέγα 3', 'κολλαγόνο', 'συμπλήρωμα διατροφής',
  // Δερμοκαλλυντικά
  'αντηλιακό', 'ενυδατική κρέμα', 'αντιγηραντική κρέμα', 'καθαριστικό προσώπου',
  'σαμπουάν', 'αφρόλουτρο', 'κρέμα χεριών', 'κρέμα ποδιών',
  // Στοματική υγιεινή
  'οδοντόκρεμα', 'οδοντόβουρτσα', 'στοματικό διάλυμα', 'μεσοδόντια βουρτσάκια',
  // Βρεφικά
  'βρεφικό γάλα', 'πάνες', 'μπιμπερό', 'πιπίλα', 'μωρομάντηλα', 'βρεφική κρέμα',
  // Ιατρικά είδη / συσκευές
  'θερμόμετρο', 'πιεσόμετρο', 'οξύμετρο', 'νεφελοποιητής', 'ζυγαριά',
  'σύριγγες', 'βελόνες', 'γάζες', 'επιθέματα', 'επίδεσμος', 'γάντια εξεταστικά',
  'αντισηπτικό', 'οινόπνευμα', 'φυσιολογικός ορός', 'τεστ εγκυμοσύνης',
  // Οφθαλμικά / ορθοπαιδικά
  'οφθαλμικές σταγόνες', 'γυαλιά πρεσβυωπίας', 'νάρθηκας', 'ορθοπαιδικά',
  'επιγονατίδα', 'περικάρπιο',
  // Λοιπά
  'κτηνιατρικά', 'εντομοαπωθητικό', 'αναλώσιμα φαρμακείου',
];

// ── Product Groups ──
// The 158 EXACT values of the `product_group` facet (Ομάδα Προϊόντων) —
// snapshot from the index 2026-06-12. This is syfak's real navigational
// taxonomy; the prompt embeds it so the agent can answer taxonomy questions
// and filter browse-style queries by exact group.
// ⚠️ Hardcoded snapshot: if syfak's taxonomy drifts, regenerate this array
// from the index facets AND re-run seed-syfak.ts (two steps — auto-fetch at
// seed time is a tracked TODO, SYFAK_INTEGRATION.md §7.1).
const PRODUCT_GROUPS: string[] = [
  "MG",
  "O.T.C.",
  "SELF TEST (ΔΩΡΕΑΝ ΔΙΑΘΕΣΗ)",
  "ΑΔΥΝΑΤΙΣΜΑΤΟΣ ΠΡΟΙΟΝΤΑ",
  "ΑΝΑΛΩΣΙΜΑ ΦΑΡΜΑΚΕΙΟΥ - ΕΤΙΚΕΤΤΕΣ",
  "ΑΝΑΛΩΣΙΜΑ ΦΑΡΜΑΚΕΙΟΥ - ΜΗΧΑΝΟΓΡΑΦΙΚΟ ΧΑΡΤΙ",
  "ΑΝΑΛΩΣΙΜΑ ΦΑΡΜΑΚΕΙΟΥ - ΜΠΛΟΥΖΕΣ",
  "ΑΝΑΛΩΣΙΜΑ ΦΑΡΜΑΚΕΙΟΥ - ΣΑΚΟΥΛΕΣ",
  "ΑΝΑΛΩΣΙΜΑ ΦΑΡΜΑΚΕΙΟΥ - ΤΑΙΝΙΕΣ ΤΑΜΕΙΑΚΗΣ",
  "ΑΞΕΣΟΥΑΡ ΑΤΟΜΙΚΗΣ ΠΕΡΙΠΟΙΗΣΗΣ - ΜΑΝΙΚΙΟΥΡ - ΠΕΝΤΙΚΙΟΥΡ",
  "ΑΞΕΣΟΥΑΡ ΑΤΟΜΙΚΗΣ ΠΕΡΙΠΟΙΗΣΗΣ - ΣΦΟΥΓΓΑΡΙΑ",
  "ΑΞΕΣΟΥΑΡ ΑΤΟΜΙΚΗΣ ΠΕΡΙΠΟΙΗΣΗΣ - ΤΣΙΜΠΙΔΑΚΙΑ",
  "ΑΞΕΣΟΥΑΡ ΑΤΟΜΙΚΗΣ ΠΕΡΙΠΟΙΗΣΗΣ - ΧΤΕΝΕΣ-ΒΟΥΡΤΣΕΣ",
  "ΑΞΙΑΚΗ ΧΡΕΩΣΗ/ΠΙΣΤΩΣΗ",
  "ΑΠΟΣΜΗΤΙΚΑ ΣΩΜΑΤΟΣ",
  "ΑΠΟΤΡΙΧΩΤΙΚΑ",
  "ΑΤΟΜΙΚΗ ΥΓΙΕΙΝΗ",
  "ΑΦΡΟΛΟΥΤΡΑ-DOUCHE",
  "ΒΡΕΦΙΚΑ - ΔΙΑΦΟΡΑ",
  "ΒΡΕΦΙΚΑ - ΘΗΛΕΣ",
  "ΒΡΕΦΙΚΑ - ΚΑΛΛΥΝΤΙΚΑ",
  "ΒΡΕΦΙΚΑ - ΚΑΡΟΤΣΑΚΙΑ",
  "ΒΡΕΦΙΚΑ - ΜΑΣΗΤΙΚΑ",
  "ΒΡΕΦΙΚΑ - ΜΠΙΜΠΕΡΟ",
  "ΒΡΕΦΙΚΑ - ΠΙΠΙΛΕΣ",
  "ΒΡΕΦΙΚΑ - ΠΡΟΙΟΝΤΑ ΘΗΛΑΣΜΟΥ",
  "ΒΡΕΦΙΚΑ - ΣΚΕΥΗ",
  "ΔΙΑΓΝΩΣΤΙΚΑ - ΔΙΑΦΟΡΑ",
  "ΔΙΑΓΝΩΣΤΙΚΑ - ΕΓΚΥΜΟΣΥΝΗΣ ΩΟΡΗΞΙΑΣ",
  "ΔΙΑΓΝΩΣΤΙΚΑ - ΣΑΚΧΑΡΟΥ ΧΟΛΗΣΤΕΡΙΝΗΣ ΤΡΙΓΛΥΚΕΡΙΔΙΑ",
  "ΔΙΑΤΡΟΦΙΚΑ - ΕΙΔΙΚΗ ΔΙΑΤΡΟΦΗ",
  "ΔΙΑΤΡΟΦΙΚΑ - ΕΝΙΣΧΥΤΙΚΑ",
  "ΔΙΑΤΡΟΦΙΚΑ - ΥΠΟΚΑΤΑΣΤΑΤΑ",
  "ΔΙΑΦΟΡΑ",
  "ΕΙΣΠΝΟΩΝ ΣΥΣΚΕΥΕΣ",
  "ΕΝΤΟΜΟΑΠΩΘΗΤΙΚΑ ΕΝΤΟΜΟΚΤΟΝΑ - ΕΝΤΟΜΟΚΤΟΝΑ ΠΑΡΑΣΙΤΟΚΤΟΝΑ",
  "ΕΝΤΟΜΟΑΠΩΘΗΤΙΚΑ ΕΝΤΟΜΟΚΤΟΝΑ - ΠΡΟΙΟΝΤΑ ΠΡΟΛΗΨΗΣ ΘΕΡΑΠΕΙΑΣ",
  "ΕΝΤΟΜΟΑΠΩΘΗΤΙΚΑ ΕΝΤΟΜΟΚΤΟΝΑ - ΣΥΣΚΕΥΕΣ ΑΞΕΣΟΥΑΡ",
  "ΕΝΤΟΜΟΑΠΩΘΗΤΙΚΑ ΕΝΤΟΜΟΚΤΟΝΑ -ΑΝΤΑΛΛΑΚΤΙΚΑ",
  "ΕΝΤΟΜΟΚΤΟΝΑ - ΕΝΤΟΜΟΑΠΩΘΗΤΙΚΑ",
  "ΕΠΙΔΕΣΜΙΚΟ ΥΛΙΚΟ",
  "ΘΕΡΜΟΦΟΡΕΣ-ΠΑΓΟΚΥΣΤΕΣ",
  "ΚΑΘΑΡΤΙΚΑ",
  "ΚΑΛΛΥΝΤΙΚΑ - ΑΔΥΝΑΤΙΣΤΙΚΑ",
  "ΚΑΛΛΥΝΤΙΚΑ - ΑΝΤΙΗΛΙΑΚΑ",
  "ΚΑΛΛΥΝΤΙΚΑ - ΜΑΚΙΓΙΑΖ",
  "ΚΑΛΛΥΝΤΙΚΑ - ΜΑΛΛΙΑ",
  "ΚΑΛΛΥΝΤΙΚΑ - ΠΟΔΙΑ",
  "ΚΑΛΛΥΝΤΙΚΑ - ΠΡΟΣΩΠΟ",
  "ΚΑΛΛΥΝΤΙΚΑ - ΣΩΜΑ",
  "ΚΑΛΛΥΝΤΙΚΑ - ΧΕΡΙΑ",
  "ΚΑΛΛΥΝΤΙΚΑ ΕΙΔΙΚΑ-ΦΑΡΜΑΚΕΥΤΙΚΑ",
  "ΚΑΡΑΜΕΛΕΣ",
  "ΚΑΤΗΓΟΡΙΑ ΓΑΛΑΤΩΝ",
  "ΚΤΗΝΙΑΤΡΙΚΑ",
  "ΜΕΘΟΔΟΙ ΦΥΣΙΚΗΣ ΘΕΡΑΠΕΙΑΣ",
  "ΜΗ ΣΥΝΤΑΓΟΓΡΑΦΟΥΜΕΝΑ - ΑΝΤΙΒΗΧΙΚΑ",
  "ΜΗ ΣΥΝΤΑΓΟΓΡΑΦΟΥΜΕΝΑ - ΑΝΤΙΔΙΑΡΟΟΙΚΑ",
  "ΜΗ ΣΥΝΤΑΓΟΓΡΑΦΟΥΜΕΝΑ - ΑΠΟΣΥΜΦΟΡΗΤΙΚ",
  "ΜΗ ΣΥΝΤΑΓΟΓΡΑΦΟΥΜΕΝΑ - ΕΜΠΛΑΣΤΡΑ",
  "ΜΗ ΣΥΝΤΑΓΟΓΡΑΦΟΥΜΕΝΑ - ΘΕΡΜΑΝΤΙΚΑ ΠΡΟΙΟΝΤΑ ΨΥΚΤΙΚΑ",
  "ΜΗ ΣΥΝΤΑΓΟΓΡΑΦΟΥΜΕΝΑ - ΚΑΡΑΜΕΛΕΣ",
  "ΜΗ ΣΥΝΤΑΓΟΓΡΑΦΟΥΜΕΝΑ - ΚΟΛΛΥΡΙΑ",
  "ΜΗ ΣΥΝΤΑΓΟΓΡΑΦΟΥΜΕΝΑ - ΠΑΥΣΙΠΟΝΑ",
  "ΜΗ ΣΥΝΤΑΓΟΓΡΑΦΟΥΜΕΝΑ - ΥΠΑΚΤΙΚΑ",
  "ΝΟΣΟΚΟΜΕΙΑΚΑ - ΑΠΟΣΤΕΙΡΩΜΕΝΟΙ ΠΕΡΙΕΚΤΕΣ",
  "ΝΟΣΟΚΟΜΕΙΑΚΑ - ΓΑΝΤΙΑ",
  "ΝΟΣΟΚΟΜΕΙΑΚΑ - ΓΛΩΣΣΟΠΙΕΣΤΡΑ",
  "ΝΟΣΟΚΟΜΕΙΑΚΑ - ΕΝΔΟΜΗΤΡΙΑ",
  "ΝΟΣΟΚΟΜΕΙΑΚΑ - ΕΠΙΠΛΑ ΣΚΕΥΗ",
  "ΝΟΣΟΚΟΜΕΙΑΚΑ - ΚΑΘΕΤΗΡΕΣ",
  "ΝΟΣΟΚΟΜΕΙΑΚΑ - ΜΑΣΚΕΣ",
  "ΝΟΣΟΚΟΜΕΙΑΚΑ - ΠΡΟΪΟΝΤΑ ΚΟΛΟΣΤΟΜΙΑΣ",
  "ΝΟΣΟΚΟΜΕΙΑΚΑ - ΡΑΜΜΑΤΑ",
  "ΝΟΣΟΚΟΜΕΙΑΚΑ - ΧΕΙΡΟΥΡΓΙΚΑ ΕΡΓΑΛΕΙΑ",
  "ΟΔΟΝΤΟΒΟΥΡΤΣΕΣ",
  "ΟΔΟΝΤΟΚΡΕΜΕΣ",
  "ΟΡΘΟΠΑΙΔΙΚΑ - ΒΟΗΘΗΜΑΤΑ",
  "ΟΡΘΟΠΑΙΔΙΚΑ - ΥΠΟΣΤΗΡΙΞΗ",
  "ΟΦΘΑΛΜΙΚΑ ΠΡΟΙΟΝΤΑ - ΓΥΑΛΙΑ",
  "ΟΦΘΑΛΜΙΚΑ ΠΡΟΙΟΝΤΑ - ΚΑΘΑΡΙΣΤΙΚΑ ΠΡΟΙΟΝΤΑ",
  "ΟΦΘΑΛΜΙΚΑ ΠΡΟΙΟΝΤΑ - ΚΟΛΛΥΡΙΑ",
  "ΟΦΘΑΛΜΙΚΑ ΠΡΟΙΟΝΤΑ - ΥΓΡΑ ΦΑΚΩΝ",
  "ΠΑΙΔ. ΤΡΟΦΕΣ - ΓΑΛΑΤΑΣ 1ης",
  "ΠΑΙΔ. ΤΡΟΦΕΣ - ΓΑΛΑΤΑΣ 2ης",
  "ΠΑΙΔ. ΤΡΟΦΕΣ - ΕΙΔΙΚΗ ΔΙΑΤΡΟΦΗ",
  "ΠΑΙΔ. ΤΡΟΦΕΣ - ΚΡΕΜΕΣ ΓΕΥΜΑΤΑ",
  "ΠΑΙΔ. ΤΡΟΦΕΣ - ΡΟΦΗΜΑΤΑ",
  "ΠΑΙΔΙΚΑ ΚΑΛΛΥΝΤΙΚΑ",
  "ΠΑΙΧΝΙΔΙΑ - ΚΟΥΔΟΥΝΙΣΤΡΕΣ",
  "ΠΑΝΕΣ ΕΝΗΛΙΚΩΝ",
  "ΠΑΝΕΣ ΜΩΡΩΝ",
  "ΠΑΡΑΪΑΤΡΙΚΑ",
  "ΠΕΡΙΠ.ΤΡΑΥΜΑΤΩΝ - ΠΡΟΪΟΝΤΑ ΑΓΩΓΗΣ",
  "ΠΕΡΙΠ.ΤΡΑΥΜΑΤΩΝ - ΠΡΟΪΟΝΤΑ ΕΠΙΔΕΣΗΣ",
  "ΠΕΡΙΠ.ΤΡΑΥΜΑΤΩΝ - ΠΡΟΪΟΝΤΑ ΕΠΙΘΕΜΑΤΑ",
  "ΠΕΡΙΠΟΙΗΣΗ",
  "ΠΟΔΙΩΝ ΠΕΡΙΠΟΙΗΣΗ",
  "ΠΡΟΙΟΝΤΑ ΑΚΡΑΤΕΙΑΣ-ΥΓΙΕΙΝΗΣ ΠΡΟΛΗΨΗΣ - ΜΑΝΤΗΛΑΚΙΑ ΜΙΑΣ ΧΡΗΣΗΣ",
  "ΠΡΟΙΟΝΤΑ ΑΚΡΑΤΕΙΑΣ-ΥΓΙΕΙΝΗΣ ΠΡΟΛΗΨΗΣ - ΠΑΝΕΣ ΒΡΕΦΙΚΕΣ",
  "ΠΡΟΙΟΝΤΑ ΑΚΡΑΤΕΙΑΣ-ΥΓΙΕΙΝΗΣ ΠΡΟΛΗΨΗΣ - ΠΡΟΦΥΛΑΚΤΙΚΑ",
  "ΠΡΟΙΟΝΤΑ ΑΚΡΑΤΕΙΑΣ-ΥΓΙΕΙΝΗΣ ΠΡΟΛΗΨΗΣ - ΥΠΟΣΕΝΤΟΝΑ",
  "ΠΡΟΙΟΝΤΑ ΑΚΡΑΤΕΙΑΣ-ΥΓΙΕΙΝΗΣ ΠΡΟΛΗΨΗΣ ΠΑΝΕΣ ΑΚΡΑΤΕΙΑΣ",
  "ΠΡΟΙΟΝΤΑ ΑΚΡΑΤΕΙΑΣ-ΥΓΙΕΙΝΗΣ ΠΡΟΛΗΨΗΣ ΣΕΡΒΙΕΤΕΣ",
  "ΠΡΟΦΥΛΑΚΤΙΚΑ",
  "ΠΡΟΩΘΗΤΙΚΟ ΥΛΙΚΟ",
  "ΣΑΚΧΑΡΟΥ κ.λ.ΜΕΤΡΗΣΗΣ ΠΡΟΙΟΝΤΑ",
  "ΣΑΝΔΑΛΙΑ-ΠΑΤΟΙ",
  "ΣΑΠΟΥΝΙΑ ΣΤΕΡΕΑ-ΥΓΡΑ",
  "ΣΕΡΒΙΕΤΕΣ-ΤΑΜΠΟΝ",
  "ΣΤΟΜΑΤΙΚΗ ΥΓΙΕΙΝΗ",
  "ΣΤΟΜΑΤΙΚΗ ΥΓΙΕΙΝΗ  - ΝΗΜΑΤΑ",
  "ΣΤΟΜΑΤΙΚΗ ΥΓΙΕΙΝΗ - ΔΙΑΛΥΜΑΤΑ",
  "ΣΤΟΜΑΤΙΚΗ ΥΓΙΕΙΝΗ - ΛΕΥΚΑΝΣΗ",
  "ΣΤΟΜΑΤΙΚΗ ΥΓΙΕΙΝΗ - ΟΔΟΝΤΟΒΟΥΡΤΣΕΣ",
  "ΣΤΟΜΑΤΙΚΗ ΥΓΙΕΙΝΗ - ΟΔΟΝΤΟΚΡΕΜΕΣ",
  "ΣΤΟΜΑΤΙΚΗ ΥΓΙΕΙΝΗ - ΟΔΟΝΤΟΤΣΙΧΛΕΣ ΑΠΟΣΜΗΤΙΚΑ",
  "ΣΤΟΜΑΤΙΚΗ ΥΓΙΕΙΝΗ - ΠΡΟΙΟΝΤΑ ΜΕΣΟΔΟΝΤΙΩΝ",
  "ΣΤΟΜΑΤΙΚΗ ΥΓΙΕΙΝΗ - ΠΡΟΙΟΝΤΑ ΟΔΟΝΤΟΣΤΟΙΧΙΩΝ",
  "ΣΤΟΜΑΤΙΚΗ ΥΓΙΕΙΝΗ - ΣΥΣΚΕΥΕΣ",
  "ΣΥΜΠΛ. ΔΙΑΤΡΟΦΗΣ -  ΑΝΤΙΟΞΕΙΔΩΤΙΚΑ",
  "ΣΥΜΠΛ. ΔΙΑΤΡΟΦΗΣ - ΑΜΙΝΟΞΕΑ ΠΡΩΤΕΪΝΕΣ",
  "ΣΥΜΠΛ. ΔΙΑΤΡΟΦΗΣ - ΒΙΤΑΜΙΝΕΣ",
  "ΣΥΜΠΛ. ΔΙΑΤΡΟΦΗΣ - ΒΟΤΑΝΑ",
  "ΣΥΜΠΛ. ΔΙΑΤΡΟΦΗΣ - ΔΙΑΙΤΗΤΙΚΑ",
  "ΣΥΜΠΛ. ΔΙΑΤΡΟΦΗΣ - ΜΕΤΑΛΛΑ ΙΧΝΟΣΤΟΙΧΕΙΑ",
  "ΣΥΜΠΛ. ΔΙΑΤΡΟΦΗΣ - ΠΡΟΒΙΟΤΙΚΑ",
  "ΣΥΜΠΛΗΡΩΜΑΤΑ ΔΙΑΤΡΟΦΗΣ",
  "ΣΥΡΙΓΓΕΣ ΒΕΛΟΝΕΣ - 10ml",
  "ΣΥΡΙΓΓΕΣ ΒΕΛΟΝΕΣ - 2,5ml",
  "ΣΥΡΙΓΓΕΣ ΒΕΛΟΝΕΣ - 20ml",
  "ΣΥΡΙΓΓΕΣ ΒΕΛΟΝΕΣ - 60ml",
  "ΣΥΡΙΓΓΕΣ ΒΕΛΟΝΕΣ - ΒΕΛΟΝΕΣ ΓΙΑ ΙΝΣΟΥΛΙΝΗ",
  "ΣΥΡΙΓΓΕΣ ΒΕΛΟΝΕΣ - ΒΕΛΟΝΕΣ ΚΟΙΝΕΣ",
  "ΣΥΡΙΓΓΕΣ ΒΕΛΟΝΕΣ - ΙΝΣΟΥΛΙΝΗΣ",
  "ΣΥΡΙΓΓΕΣ ΒΕΛΟΝΕΣ - ΤΡΟΦΗΣ",
  "ΣΥΡΙΓΓΕΣ-ΒΕΛΟΝΕΣ",
  "ΣΥΣΚΕΥΑΣΙΑΣ ΕΙΔΗ",
  "ΣΥΣΚΕΥΕΣ - ΑΝΑΠΝΕΥΣΤΙΚΟΥ",
  "ΣΥΣΚΕΥΕΣ - ΔΙΑΦΟΡΑ",
  "ΣΥΣΚΕΥΕΣ - ΖΥΓΑΡΙΕΣ",
  "ΣΥΣΚΕΥΕΣ - ΘΕΡΜΟΜΕΤΡΑ",
  "ΣΥΣΚΕΥΕΣ - ΜΑΣΑΖ ΒΕΛΟΝΙΣΜΟΣ",
  "ΣΥΣΚΕΥΕΣ - ΞΥΡΙΣΜΑ ΑΠΟΤΡΙΧΩΣΗ",
  "ΣΥΣΚΕΥΕΣ - ΠΙΕΣΟΜΕΤΡΑ",
  "ΣΥΣΚΕΥΕΣ - ΣΥΣΚΕΥΕΣ ΕΡΓΑΣΤΗΡΙΟΥ",
  "ΦΑΚΩΝ ΕΠΑΦΗΣ ΕΙΔΗ",
  "ΦΑΡΜΑΚΑ",
  "ΦΑΡΜΑΚΑ-0003",
  "ΧΑΡΤΙΚΑ-ΥΠΟΣΕΝΔ.-ΒΑΜΒΑΚΙ",
  "ΧΕΙΛΙΩΝ ΠΕΡΙΠΟΙΗΣΗ",
  "ΧΗΜΙΚΑ",
  "ΧΗΜΙΚΕΣ ΠΡΩΤΕΣ ΥΛΕΣ - ΑΙΘΕΡΙΑ ΕΛΑΙΑ",
  "ΧΗΜΙΚΕΣ ΠΡΩΤΕΣ ΥΛΕΣ - ΚΟΝΕΙΣ",
  "ΧΗΜΙΚΕΣ ΠΡΩΤΕΣ ΥΛΕΣ - ΣΤΕΡΕΑ",
  "ΧΗΜΙΚΕΣ ΠΡΩΤΕΣ ΥΛΕΣ - ΥΓΡΑ",
  "ΧΩΡΙΣ ΟΜΑΔΑ",
  "ΩΤΟΚΑΘΑΡΙΣΤΕΣ-ΩΤΟΑΣΠΙΔΕΣ",
];

// ── System Prompt Builder ──
function buildSyfakPrompt(categories: string[], concepts: string[]): string {
  return `You are the digital ordering assistant for syfak, a Greek CENTRAL PHARMACY WHOLESALER (B2B). Your users are PHARMACISTS and pharmacy staff ordering stock at wholesale prices — professionals, not patients. ALWAYS respond in Greek.

## HOW THE UI WORKS
The user sees a PRODUCT GRID next to the chat. The grid updates ONLY when you call searchProducts. If you don't call the tool, the user sees the SAME products — even if you talk about different ones.

## WHEN TO CALL searchProducts
ALWAYS call searchProducts when the user:
- Asks for products ("θέλω αντηλιακά", "δείξε μου σύριγγες ινσουλίνης", "Depon")
- Searches by active substance ("σκευάσματα με παρακεταμόλη", "τι έχει amoxicillin")
- Gives an ΕΟΦ code, ATC code, brand, or manufacturer
- Narrows preferences ("μόνο διαθέσιμα", "μέχρι 5€", "σε σιρόπι", "του ΙΦΕΤ")

NEVER mention specific products without calling searchProducts IN THIS TURN — UNLESS you are recommending from previous results.

EXCEPTION — Do NOT call the tool for: greetings, thanks, general questions, clarifying questions.

### PRICE REQUESTS — never filter by price
You do NOT know the pharmacy's negotiated prices; the catalogue price you see is a reference value, not what they pay. So NEVER put a price filter in a searchProducts call — the results would be selected by prices that do not apply to this customer.
- **ABSOLUTE** ("μέχρι 5€", "2-10€") → search for the PRODUCTS they asked about, ignoring the amount, and say plainly that you can't filter by price because their prices are agreed per pharmacy — they can see each price on the product cards.
- **RELATIVE** ("φθηνότερο", "πιο οικονομικό", "κάτι ακριβότερο") → do NOT search; recommend from the previous results using priceRange and cheapestItems, making clear these are reference prices. Still link every product you name as [name](url) (the url is in cheapestItems) so it stays clickable and pinned in the grid.

## PRODUCT TITLE CONVENTIONS (critical)
Titles are abbreviated wholesale pharma names, mostly UPPERCASE Latin:
- "DEPON TAB EFF MAXIMUM 1gr x 8" — brand, form, strength, pack size
- "PARACETAMOL INJ 10mg/ml 100ml x1 KABI" — substance, form, strength, maker
- Form abbreviations: TAB (δισκία), CAPS (κάψουλες), SIR/SYR (σιρόπι), INJ (ενέσιμο), AMP (αμπούλες), CR (κρέμα), GEL, SOL (διάλυμα), GTT (σταγόνες), SUPP (υπόθετα), EFF (αναβράζοντα), SPRAY
- Pack notation: "x16", "x 20", "30tabs", "150ml"
Use the BRAND or SUBSTANCE as searchTerm, as written in titles (usually Latin): "depon", "panadol", "betadine". Greek works for parapharmacy terms ("αντηλιακό", "γάζες").

## ACTIVE SUBSTANCES ARE LATIN
The drastiki_ousia field holds INN names in LATIN UPPERCASE: PARACETAMOL, AMOXICILLIN, IBUPROFEN.
- If the user types a Greek substance name ("παρακεταμόλη"), CONVERT it to the Latin INN and filter: ["drastiki_ousia: PARACETAMOL"].
- "Εναλλακτικά / άλλα σκευάσματα με την ίδια ουσία" → filter by drastiki_ousia with the substance as searchTerm too.

## searchProducts PARAMETERS

### searchTerm (1-2 words)
- Brand or substance as written in titles: "depon", "paracetamol", "betadine"
- Greek parapharmacy terms: "αντηλιακό", "θερμόμετρο", "γάζες"
- ΕΟΦ/code lookups: the code itself ("918960101")
- NEVER generic-only words: "προϊόν", "κάτι", "φάρμακο" alone

### categories (EXACT names, use SPARINGLY)
Only 7 coarse top-level classes exist; most of the catalogue is "Παραφάρμακα". Use them ONLY when the intent clearly maps:
- prescription drugs → ["Φάρμακα Συντ/μενα"]
- OTC drugs → ["Φάρμακα μη συντ/μενα"]
- infant/special milk → ["Γάλατα"]
- supplements/cosmetics/devices/consumables → ["Παραφάρμακα"] or leave EMPTY
For brand/substance/code queries leave categories EMPTY — the text search is precise.

### Browse by PRODUCT GROUP (Ομάδα Προϊόντων) — the store's REAL taxonomy
The product_group field is the wholesaler's own taxonomy (full list at the end of this prompt).
- Browse-style queries ("τι έχετε σε καλλυντικά μαλλιών;", "δείξε μου ορθοπαιδικά υποστήριξης") → add filters: ["product_group: ΚΑΛΛΥΝΤΙΚΑ - ΜΑΛΛΙΑ"] using the EXACT name from the list, AND keep a meaningful searchTerm.
- Taxonomy questions ("τι ομάδες έχετε σε βρεφικά;") → answer directly from the list (e.g. the ΒΡΕΦΙΚΑ - * groups); optionally also search the broad term.
- ONLY filter by product_group when a listed group CLEARLY matches the intent — a wrong group filter returns nothing. If unsure, skip it; the text search works without it.

### keywords (3-5)
Mix Greek + Latin synonyms that appear in titles: "αντηλιακό" → ["sunscreen", "spf", "sun cream", "αντιηλιακή προστασία"]

### filters
- NEVER a price filter (see "PRICE REQUESTS" above) — the server drops them anyway.
- Manufacturer (κατασκευαστής/εταιρεία): ["brand: ΒΙΑΝ Α.Ε."]
- Active substance (LATIN INN): ["drastiki_ousia: PARACETAMOL"]
- ATC code: ["atc: N02BE01"]
- Product group (EXACT name from the list at the end): ["product_group: ΚΑΛΛΥΝΤΙΚΑ - ΑΝΤΙΗΛΙΑΚΑ"]
- Availability — EXACT values: ΜΗΔΕΝΙΚΟ ΑΠΟΘΕΜΑ (zero stock), ΠΕΡΙΟΡΙΣΜΕΝΗ ΔΙΑΘΕΣΙΜΟΤΗΤΑ. Products in normal stock carry NO availability value at all.
  - "διαθέσιμα" / "μόνο διαθέσιμα" / "να υπάρχει στοκ" → ALWAYS ["availability != \\"ΜΗΔΕΝΙΚΟ ΑΠΟΘΕΜΑ\\""] — never filter on ΠΕΡΙΟΡΙΣΜΕΝΗ unless the user NAMES it
- Properties — EXACT values: Ψυγείου, Συνταγογραφούμενο, Φάρμακο, ΙΦΕΤ, ΜΗΣΥΦΑ, Νοσοκομειακό, Ελλειπτικό, Δέσμευση, Κοντόληξο
  - "προϊόντα ψυγείου" → ["properties: Ψυγείου"]
  - "συνταγογραφούμενα" → ["properties: Συνταγογραφούμενο"]

## B2B CONSULTANT MINDSET
You are a procurement assistant for professionals:
- Clarify strength/form/pack ONLY when truly ambiguous — max 1 short question; B2B users value speed. When in doubt, search first.
- When recommending, mention form, strength, pack size, and AVAILABILITY.
- ΜΗΔΕΝΙΚΟ ΑΠΟΘΕΜΑ = out of stock at the wholesaler. Say so plainly and offer alternatives (same drastiki_ousia).
- A product with NO availability value is in normal stock — call it «διαθέσιμο». NEVER invent a level (no «υψηλή/μεσαία διαθεσιμότητα» — those no longer exist). Stock can differ per pharmacy; the badge on each product card shows THIS pharmacist's live status, so point to it when it matters.
- Prices shown are wholesale/RRP reference values — you do NOT know per-customer negotiated prices; never promise final pricing.
- NEVER give medical, dosing, or therapeutic advice. You help FIND and ORDER products; clinical decisions belong to the pharmacist.

## CONTEXT MANAGEMENT
- New product type → FRESH search params. Do NOT inherit keywords/categories/filters.
- Refinement of the same product → keep the same searchTerm/categories, change only filters.
- Relative words ("φθηνότερο", "άλλη εταιρεία") refer to the SAME product type you just showed.
- When unsure, ASK one brief question instead of guessing.

## RESPONSES
- Concise Greek (2-4 sentences); bullets (-) for lists.
- **ALWAYS link every specific product you name as a markdown link [name](url) — never plain text — with its availability and price inline. Example: "- [DEPON.SUPP 200mg x 6](https://…) — υψηλή διαθεσιμότητα, 2,50€". This applies EVEN when recommending from previous results: each product's url is in the tool result / cheapestItems, so always use it. Linking is what lets the user click through to the product AND pins it to the top of the product grid — a product you name in plain text is invisible in the grid.**
- Do NOT quote the raw total count as if every result is the asked product — use natural openers ("Βρήκα επιλογές...", "Ορίστε μερικά...").
- NEVER say "δεν υπάρχει"/"δεν βρέθηκε" when the search returned products — scan the titles first; pharma naming varies.

## SUGGESTIONS
At the END of your response, when useful, add a line of up to 4 short refinement chips in this exact format
(replace the placeholders with real chips — do NOT output the placeholders literally):
[SUGGESTIONS: chip, chip, chip, chip]
- A chip is a way to narrow the CURRENT results into a meaningful subset the user might pick next.
- Chips must be PRODUCT TYPES, categories, or product concepts only — a form, a use, a sub-type, an active substance.
- GROUND every chip in what actually exists in THIS result set: prefer the real categories / subcategories /
  product groups present in the products you just found (you can see them in the tool result — topCategories,
  each product's category/subcategory, the facet values), or a closely related real catalogue name. A chip must
  return products when tapped — never invent a label that has no matching products.
- NEVER suggest availability/stock chips (οτιδήποτε σχετικό με διαθεσιμότητα, π.χ. "Μόνο διαθέσιμα") or price/offer
  chips (π.χ. "Φθηνότερα", εύρη τιμών, προσφορές). These are NOT refinement topics.
- Keep each chip short (1–3 words). Omit the SUGGESTIONS line entirely if no genuinely useful refinement exists.

## PRODUCT CATEGORIES (use ONLY these exact names)
${JSON.stringify(categories)}

## PRODUCT CONCEPTS (flexible — suggest beyond this list too)
${JSON.stringify(concepts)}

## PRODUCT GROUPS (Ομάδα Προϊόντων — EXACT names for product_group filters)
${JSON.stringify(PRODUCT_GROUPS)}
`;
}

export const syfakConfig: ClientConfig = {
  // ── Identity ──
  clientId: 'syfak',
  storeName: 'syfak.gr',
  storeDescription: 'a Greek pharmacy B2B wholesale store',

  // ── Features ──
  // Pharmacists scan the box — see lib/barcodeScan.ts. Kept in sync with
  // syfak.code.ts, which is what the live DB path actually reads.
  barcodeScan: true,

  features: {
    hybridSearch: false, // Pure lexical — no embeddings (cheap crawl)
    agentChat: true,     // AI ordering assistant (enabled 2026-06-12)
    addToCart: true,     // Phase 2 (2026-07-22): normal products add via host cart.add; offer products open the offer popup
    favorites: false,    // syfak: hide the heart/favorites button on cards
    externalPricing: true, // Phase 2 (2026-07-22): per-customer pricing endpoint confirmed — see the externalPricing block below. NOTE: the runtime reads this from Supabase; this file is the birth source + DB-unreachable fallback. The LIVE switch is a DB write to features_enabled.externalPricing + external_pricing_config (+ a re-crawl so `id` becomes filterable).
    // Option B embed (acalight pattern): syfak's beta page already has its own
    // search trigger. The host wraps our #ai-search-container in a hidden
    // wrapper and reveals it on click; the widget detects the visibility change
    // and opens the modal directly (no inline trigger bar). Pairs with the
    // openSearch()/closeSearch() contract in WIDGET_EMBED_GUIDE.md.
    autoOpen: true,
  },

  // ── Embedding & Integration ──
  // Embedded on syfak's beta page via script_injection (widget.js). The widget
  // calls our APIs cross-origin from this host, so the origin MUST be in
  // allowed_origins or CORS blocks every request. NOTE: the browser Origin
  // header carries NO trailing slash, and lib/cors.ts does an exact match — so
  // this value must be slash-less ('…gr', not '…gr/').
  // (cart/externalPricing remain Phase-2 and stay off.)
  // Both the beta (demod4u) and the real production site. NOTE: the browser
  // Origin header has NO trailing slash and lib/cors.ts does an exact match, so
  // these are slash-less. If prod is served from www.syfakb2b.gr (or another
  // subdomain) too, add that exact origin here as well.
  // syfakb2b.gr (production) removed 2026-06-30: syfak rolled back to their own
  // search there, but the orphaned embed kept hammering our API. Only the staging/
  // demo origin remains. (Also hard-blocked server-side — see lib/cors.ts
  // DECOMMISSIONED_EMBED_HOSTS.) Re-add when they re-launch with us.
  allowedOrigins: ['https://syfakb2b.demod4u.gr'],
  // Not live yet — syfakb2b.demod4u.gr is the STAGING host, so all its traffic is demo.
  // (The production embed on syfakb2b.gr was decommissioned and is hard-blocked in
  // lib/cors.ts.) ⚠️ At the Phase-2 relaunch, editing this file is NOT the switch — the
  // runtime reads config from Supabase (this file is only the birth source + a fallback for
  // when the DB is unreachable). Do a DIRECT DB WRITE to clients.production_origins (and
  // allowed_origins). See "Runbook: Set Up a New Client" step 8.
  productionOrigins: [],
  hostingStrategy: 'script_injection',
  // Phase 2 (2026-07-22): normal products add via the host's OpenCart cart.add
  // (like acalight). OFFER products (customer price carries offer_url) instead
  // show "ΕΠΙΛΕΞΕ ΠΡΟΣΦΟΡΑ" → the offer popup, whose own button calls the host's
  // prosfores.add — see ProductCard + OfferPopup. Runtime reads cart from
  // Supabase; the live switch is a DB write to cart_config (done on dev).
  // flyToCartSelector: syfak's header basket (div#cart with its .cart_counter
  // badge — inspected live 2026-07-27); a successful add flies a ghost dot to
  // it, mirroring their own listing pages' feedback. Their header stays visible
  // above our modal, so the pharmacist also sees the badge count tick up.
  // ⚠️ `cart.addLiveQuantity`, NOT the OpenCart-standard `cart.add`.
  //
  // syfak's team reported (2026-08) that adding a normal ΤΙΜΗ ΡΟΗΣ product did
  // nothing, while offer products added fine. Their own catalog JS is the proof:
  // in `catalog/view/theme/javascript/lists.js` every add-to-cart button calls
  //   row.hasClass('prosfores') ? prosfores.addLiveQuantity(id, qty)
  //                             : cart.addLiveQuantity(id, qty)
  // and `cart.add(` is called ZERO times anywhere in their JavaScript — it is
  // leftover stock-OpenCart code in common.js. The two hit different endpoints:
  // `cart.add` → checkout/cart/cart/addcartrois, `addLiveQuantity` →
  // checkout/cart/cart/addCartRoisLive ("rois" = ροής, the flow-price basket).
  //
  // Offer products were unaffected because their popup is syfak's own markup and
  // carries syfak's own `prosfores.add` onclick — the one path we never built.
  //
  // Our checks missed it because `window.cart.add` EXISTS, so the dispatcher
  // resolved it, returned success, and the button showed «✓ Προστέθηκε» for an
  // add that never happened. Only a real order would have caught it.
  //
  // ⚠️ The runtime reads this from the Supabase `cart_config` column, so
  // changing this file alone does NOT fix prod — the DB row must be updated too.
  cart: { strategy: 'opencart_global', functionName: 'cart.addLiveQuantity', flyToCartSelector: '#cart' },

  // ── External Pricing (per-customer B2B) — Phase 2, enabled 2026-07-22 ──
  // Same endpoint family as acalight (confirmed by syfak's team + a live staging
  // test): a relative URL is same-origin on the host, so the OpenCart session
  // cookie identifies the logged-in pharmacy and we never see prices. The
  // RESPONSE differs from acalight though — syfak returns a HYBRID: a
  // [false,false] array for no-price items, and a named-field object
  // { price, special, offer_url, stock_text, labels } for priced ones. So
  // responseFormat is 'syfak_v1' (parseSyfakV1 in lib/pricing/externalPricing.ts),
  // NOT 'opencart_v1'. (offer_url = the month-offer popup, wired in a later
  // custom card layer; the pricing basics use price/special only.)
  externalPricing: {
    endpointUrl: '/index.php?route=search_results/ajax_get_prices',
    requestMethod: 'GET',
    idParam: 'products',
    idSeparator: ',',
    credentialsMode: 'same-origin',
    responseFormat: 'syfak_v1',
    // 10s: the host prices per id (syfak staging ~57ms/id → ~6s for the 100-id
    // priority window). Read for real since 2026-08-31 — see DEFAULT_DISPLAY_PRICE_TIMEOUT_MS.
    timeoutMs: 10000,
    // 100 because that is what SYFAK'S OWN WEBSITE uses against this endpoint —
    // told to us by their team 2026-07-28. That beats any number we can measure
    // from outside: it is the batch size their server is actually built and
    // tuned for, day in day out.
    //
    // Our previous 500 was an outside guess from probing their live endpoint
    // with a logged-in account (2026-07-27): 600 ids answered fine, 800 came
    // back 403 under production conditions, so 500 kept a margin. That probing
    // also established WHY the ceiling exists — per-id B2B computation cost, not
    // URL length: logged OUT, 800 ids and even 32 concurrent requests are
    // accepted instantly, because no work is done. So the limit is invisible to
    // any unauthenticated test, which is exactly why their own number is worth
    // more than ours.
    //
    // Smaller batches mean more requests for a large result set, but that no
    // longer costs anything: the widget only prices what the shopper has
    // actually scrolled to, so a typical search is ONE request of 100.
    chunkSize: 100,
    // ⛔ Full-catalogue price map DISABLED for syfak (2026-07-27, founder call,
    // on measured evidence). Their server cannot absorb pricing 25,417 products
    // per visit: the build got HTTP 503 with requests left pending, and — the
    // real damage — it STARVED the interactive fetches on the same origin, so
    // cards fell back to catalogue prices, offers disappeared and the offer
    // popup crawled. A page refresh appeared to "fix the login" only because it
    // aborted our own storm. The map was failing regardless, so this costs only
    // the exact price-band counts (already unavailable) and buys back reliable
    // per-customer prices + offers. RE-ENABLE when their one-shot "all prices"
    // endpoint lands (FreeRide backlog 34c60ea0) — the ask is in e01272d6.
    disablePriceMap: true,
  },

  // ── Meilisearch ──
  indexName: 'products_syfak',
  embedderName: 'syfak-openai', // Parity only — unused while hybridSearch is false
  locale: 'ell',

  // ── Feed ──
  feedUrl: 'https://syfakb2b.gr/product_feed-el-gr.xml',
  productLimit: 30000, // ~25k active SKUs + buffer
  feedFormat: 'syfak_xml',

  // ── Dynamic Facet Fields ──
  // Universal brand / category / price are automatic. `subcategory` is kept here
  // (xmlFilterKey null) so it stays filterable; the universal category UI renders
  // it and buildCustomFilterGroups skips it (no duplicate group). The other six
  // are pharma-specific: three synthesised (sentinel xmlFilterKeys, produced in
  // syfakXml.ts) and three from the feed's <filters> block.
  facetFields: [
    { field: 'subcategory',    label: 'Υποκατηγορία',   xmlFilterKey: null,                  isArray: true },
    { field: 'product_group',  label: 'Ομάδα Προϊόντων', xmlFilterKey: '__product_group__',   isArray: true },
    { field: 'availability',   label: 'Διαθεσιμότητα',   xmlFilterKey: '__availability__',    isArray: true },
    { field: 'properties',     label: 'Ιδιότητες',       xmlFilterKey: '__properties__',      isArray: true },
    { field: 'drastiki_ousia', label: 'Δραστική Ουσία',  xmlFilterKey: 'Δραστική ουσία',      isArray: true },
    { field: 'atc',            label: 'Κωδικός ATC',     xmlFilterKey: 'Κωδικός ATC',         isArray: true },
    { field: 'characterization', label: 'Χαρακτηρισμός', xmlFilterKey: 'Χαρακτηρισμός',       isArray: true },
  ],

  // ── Per-warehouse scoping (2026-08-31) ──
  // syfak serves five warehouses and a pharmacist is attached to exactly one of
  // them. The feed's <compdata> block says, per product, which warehouses carry
  // it; their page tells us the customer's warehouse via `data-warehouse` on the
  // embed script tag. Products the customer's warehouse does not carry are hidden
  // from their results entirely (founder's decision, 2026-08-31).
  //
  // NOT a facetField on purpose — the warehouse comes from the login, so it is
  // never a sidebar choice. getFilterableAttributes adds the field regardless.
  //
  // Measured scale (feed 2026-08-31, 25,485 products): code 01 (Ηράκλειο) carries
  // 100% of the catalogue and is a strict superset of every other warehouse, so it
  // hides nothing; the four branches hide 120–161 products each (~0.6%).
  warehouseScope: {
    // Deliberately NOT `warehouses_available` — acalight already uses that exact
    // field name for something different (its user-facing «Διαθεσιμότητα Αποθήκης»
    // sidebar facet, values = warehouse NAMES). Separate clients and separate
    // indexes, so nothing could actually collide — but two fields with one name
    // meaning two different things is how a future reader gets it wrong. These
    // values are CODES ("01"), so the name says so.
    field: 'warehouse_codes',
    knownCodes: ['01', '02', '03', '04', '05'],
  },

  // ── Search Tuning ──
  search: {
    semanticRatio: 0.0, // Pure lexical
    // 0.60 (was 0.65): exact ATC-code matches land at 0.625 — just under 0.65 —
    // because `atc` is a low-priority array field. 0.60 lets code lookups through
    // (ΕΟΦ/SKU already clear it) with no change to text-search results (verified:
    // identical hit counts for "αντηλιακό"/"depon" at 0.60 vs 0.65).
    // 0.45 (was 0.60, 2026-08-25) — measured on the real 25k catalogue against
    // 28 REAL user queries from analytics: 8 better, 0 worse, 20 unchanged.
    //
    // Meilisearch scores are BIMODAL: ~0.93–1.00 for an exact title match, then
    // ~0.49–0.53 for everything else, with nothing in between. So 0.60 was not
    // a quality dial at all — it was a binary "exact title matches only" switch,
    // and any value from 0.55 to 0.90 behaves identically. The recovery only
    // happens below ~0.5.
    //
    // What that switch was discarding is mostly CORRECT: for «οδοντ», 451 of
    // the deleted results matched `product_group` EXACTLY (real dental products
    // whose titles are in English — toothpaste, mouthwash). Also how
    // Meilisearch's own Greek stemmer reaches other inflections of a word: a
    // stem match scores 0.497, so the stemmer was working and we were binning
    // its output. Effect: οδοντ 261→712, βρεφ 859→911, misspelt «καλιντ» 0→21.
    //
    // ⚠️ NEVER lower this without the typoTolerance oneTypo:6 above. On its own
    // it also admits TYPO matches on titles, which for «depon» means DEPOLAN,
    // DEPOMEDROL and hair dye — 12 wrong medicines. The pair is safe; either
    // half alone is worse than neither.
    minScoreThreshold: 0.45,
    fallbackScoreThreshold: 0.75,
    maxResultsToFetch: 1000,
    maxResultsToReturn: 1000,
  },

  // ── Agent Search Tuning ──
  // minKeywordLength/minTermLength 3 (not 4): titles are Latin-heavy with
  // short meaningful tokens (spf, gel, tab) — same reasoning as kaizershop.
  agentSearch: {
    minScoreThreshold: 0.75,
    layer4ScoreThreshold: 0.7,
    layer1: { limit: 100, matchingStrategy: 'last' },
    layer2: { maxCategories: 15, totalBudget: 80, minPerCategory: 12, matchingStrategy: 'last' },
    layer3: { limit: 20, maxKeywords: 5, minKeywordLength: 3, matchingStrategy: 'last' },
    layer4: { limit: 120, matchingStrategy: 'all' },
    discovery: { minTermLength: 3, matchingStrategy: 'last' },
    maxProductsToReturn: 200,
  },

  // ── Language ──
  language: {
    stopWords: [
      'ο', 'η', 'το', 'τα', 'του', 'τις', 'τον',
      'κε', 'για', 'σε', 'με', 'απο', 'προς',
    ],
    synonyms: {
      // "T4" = thyroxine / levothyroxine. This catalogue names the SAME thyroid
      // drug four ways: "T4", "T 4" (spaced), "THYRO-4", "L-THYROXIN". A bare
      // "t4" search only hit the literal "T4" items (2). Map the shorthand to
      // the active substance so it finds the whole levothyroxine line, and unify
      // the spaced/unspaced forms. (Greek "τ4" already equals "t4" via the
      // homoglyph fold, so no Greek mapping is needed.)
      // NOTE: synonyms are a Meilisearch index setting — applied on the next
      // crawl, or via a one-off settings PUT (see SYFAK_INTEGRATION.md).
      't4': ['t 4', 'levothyroxine'],
      't 4': ['t4', 'levothyroxine'],
      // Sunscreens are titled in English (SUN / sunscreen / spf), so a Greek
      // "αντηλιακα" search finds almost nothing. Map it to the English terms.
      // Synonyms match the QUERY as the search route sends it (normalizeGreek
      // output): "αντηλιακα"→"αντιλιακ", "ΑΝΤΙΗΛΙΑΚΑ"→"αντιιλιακ". Powers both
      // the ΑΝΤΙΗΛΙΑΚΑ top-search chip and free-text sunscreen searches.
      'αντιλιακ': ['sun', 'sunscreen'],
      'αντιιλιακ': ['sun', 'sunscreen'],
      // Paracetamol is in the catalogue only as the Latin INN ("PARACETAMOL",
      // in titles + the drastiki_ousia active-substance field) — a Greek
      // "παρακεταμόλη" search hit 0. Map the Greek term to the Latin one.
      // As-you-type: the synonym only fires when the typed word EQUALS a key, so
      // a single key (παρακεταμολ) leaves a "0 results" dead zone while typing
      // (παρακετ…παρακεταμο). Add the intermediate stems so paracetamol appears
      // smoothly from "παρακετ" onward. Safe — nothing else stems to παρακετ/
      // παρακεταμ (the puncture needles "παρακέντηση" stem to παρακεντισ). The
      // typed prefixes collapse via normalizeGreek: παρακετα→παρακετ,
      // παρακεταμο→παρακεταμ, παρακεταμόλη/‑ολη→παρακεταμολ — so these 3 keys
      // cover every length from 7 chars to the full word.
      'παρακετ': ['paracetamol'],
      'παρακεταμ': ['paracetamol'],
      'παρακεταμολ': ['paracetamol'],
    },
    queryStopWords: [
      'από', 'απο', 'με', 'για', 'σε', 'και', 'στο', 'στη', 'στα',
      'στον', 'στην', 'του', 'της', 'των', 'τον', 'την', 'τους', 'τις',
      'ο', 'η', 'το', 'οι', 'τα', 'ένα', 'ενα', 'μια', 'μία', 'προς',
    ],
    pricePatterns: {
      maxPrice: /(?:μέχρι|μεχρι|έως|εως|κάτω από|κατω απο|κατω|κάτω|up to|under|below)\s+(\d+(\.\d+)?)\s*(?:ευρω|ευρώ|€|euros?)/i,
      minPrice: /(?:πάνω από|πανω απο|πανω|πάνω|από|απο|over|above)\s+(\d+(\.\d+)?)\s*(?:ευρω|ευρώ|€|euros?)/i,
      range: /(?:από|απο|from)?\s*(\d+(?:\.\d+)?)\s*(?:-|–|έως|εως|μέχρι|μεχρι|to)\s*(\d+(?:\.\d+)?)\s*(?:ευρω|ευρώ|€|euros?)/i,
    },
    noisyPhrases: [],
  },

  // ── Index Settings ──
  indexSettings: {
    // No description (empty/polluted in this feed). drastiki_ousia / atc / eof /
    // model are searchable so B2B buyers can find by active substance or code.
    searchableAttributes: [
      'title', 'title_normalized',
      'brand',
      'model',
      'category', 'category_normalized',
      'subcategory', 'subcategory_normalized',
      // 'properties' (pharma flags: ΙΦΕΤ, Ψυγείου, Συνταγογραφούμενο, …) ranked
      // here, mid-list, so the ΙΦΕΤ top-search chip returns the full flagged set.
      // A flag matched only in a LAST-ranked attribute scores below the route's
      // minScoreThreshold and gets dropped (that's why ΙΦΕΤ returned ~5). These
      // flag words rarely collide with product-name searches.
      //
      // ⚠️ The `_normalized` twins below are what actually make these findable
      // (added 2026-07-28). Their raw counterparts hold the ORIGINAL Greek
      // spelling, but a typed query is phonetically normalized first, so
      // "ελλειπτικο" → "ελλιπτ" never lined up with "ΕΛΛΕΙΠΤΙΚΟ". Measured
      // before the twins existed: ΙΦΕΤ → 890 (an acronym, unchanged by
      // normalization) but ΕΛΛΕΙΠΤΙΚΟ and ΚΟΝΤΟΛΗΞΙΜΟ → 0. Keep each twin next
      // to its raw field so both spellings rank the same.
      'properties',
      'properties_normalized',
      // Χαρακτηρισμός — was NOT searchable at all until 2026-07-28. It is the
      // field whose wording matches the card's status tags exactly.
      'characterization',
      'characterization_normalized',
      'drastiki_ousia',
      'atc',
      'eof',
      'barcode',
      'id',
      // product_group LAST — ΚΑΛΛΥΝΤΙΚΑ/ΑΝΤΙΗΛΙΑΚΑ etc. also appear in titles, so
      // a low priority is enough and keeps normal-search ranking clean.
      // NO `product_group_normalized`: tried 2026-07-28 and measured as a
      // regression (« βιταμινη c » 5 → 919 results). See GREEK_SEARCHABLE_FACETS
      // in lib/crawler/parsers/syfakXml.ts for the full reasoning.
      'product_group',
    ],
    // Display everything indexed — many dynamic facet arrays are needed by the
    // sidebar chips; '*' avoids an explicit whitelist to keep in sync.
    displayedAttributes: ['*'],
    rankingRules: [
      'words', 'typo', 'proximity', 'attribute', 'sort', 'exactness',
    ],
    localizedAttributes: [
      {
        locales: ['ell'],
        attributePatterns: ['title', 'category', 'subcategory'],
      },
    ],
    // Unused while hybridSearch is false, but required by the type.
    documentTemplate: '{{doc.category}} - {{doc.brand}} {{doc.title}}',
    faceting: {
      maxValuesPerFacet: 500,
      sortFacetValuesBy: { '*': 'count' as const },
    },
    // ⚠️ 6, not the engine default 5 — a deliberate, measured choice for a PHARMACY.
    //
    // At 5, a five-letter drug name gets one free wrong letter, so «DEPON»
    // (paracetamol — their single most-searched term) also matched DEPOLAN,
    // DEPOMEDROL, DEPOMEDRONE and DEPEND ΒΑΦΗ (hair dye). Measured on the real
    // catalogue: 35 results of which 19 were the wrong medicine. That is a
    // safety problem, not a relevance one.
    //
    // At 6 the drug names are exact again (depon → 16 results, all DEPON,
    // every one scoring 1.00) while longer words keep their typo forgiveness —
    // «καλιντ» for καλλυντικά still returns 21. 7 was tested too and was worse:
    // it takes «καλιντ» to 0.
    //
    // This value is what makes the lowered search.minScoreThreshold (0.45)
    // safe; the two were validated together and should not be separated.
    //
    // NOTE: until 2026-08-25 this block was never sent to Meilisearch at all
    // (see applyIndexSettings) — the index ran the 5/9 default. The old 4/8
    // written here had never taken effect and was never measured.
    typoTolerance: {
      minWordSizeForTypos: { oneTypo: 6, twoTypos: 10 },
    },
  },

  // ── System Prompt (stub — agent off) ──
  systemPrompt: {
    categories: PRODUCT_CATEGORIES,
    productConcepts: PRODUCT_CONCEPTS,
    buildPrompt: buildSyfakPrompt,
  },

  // ── UI Configuration ──
  ui: {
    // PLACEHOLDER: replace with syfak's real brand color/hover once provided.
    brandColor: '#0a8a5f',      // placeholder pharmacy green
    brandColorHover: '#097a53',
    scrollbarColor: '#0a8a5f',
    // Open product links in the SAME window (the host page) instead of a new tab.
    // syfak's choice — note this unloads the widget/modal on navigation.
    productLinkTarget: '_self',
    // Pharmacists asked for the list view by default (2026-09-28). A shopper who
    // switches to grid keeps it on that device.
    defaultViewMode: 'list',
    // «Διαθεσιμότητα» stays indexed (Αριάδνη filters on it; the badge falls back
    // to it) but is hidden from the sidebar (2026-10-01): since syfak's label
    // change, in-stock products carry no value, so the group could only offer
    // «ΜΗΔΕΝΙΚΟ ΑΠΟΘΕΜΑ»/«ΠΕΡΙΟΡΙΣΜΕΝΗ» — not the "in stock" pharmacists used it
    // for — and it is FEED-based, so it contradicted the per-customer badge on
    // ~1 product in 5. Bring back once syfak sends per-warehouse stock.
    hiddenFacets: ['availability'],
    fontCssVariable: '--font-gotham-greek',
    // The feed has no images for 99.5% of products — show the pharmacy
    // "NO IMAGE" placeholder instead of the generic icon.
    fallbackImageUrl: '/syfak-fallback.png',
    // Availability badge on cards — colors sampled from syfakb2b.gr's own
    // product-list labels so the widget matches their site.
    //
    // 2026-10-01: syfak cut the vocabulary to «ΜΗΔΕΝΙΚΟ ΑΠΟΘΕΜΑ» (zero stock,
    // same #006b99 as the old ΕΛΛΕΙΨΗ — sampled from their live site) and
    // «ΠΕΡΙΟΡΙΣΜΕΝΗ ΔΙΑΘΕΣΙΜΟΤΗΤΑ»; normal stock now carries no label. The old
    // keys stay: their staging site still sends them. The label is read PER
    // CUSTOMER from stock_text (their developer's request — it is what their own
    // site shows), and an unknown label still shows in neutral grey, so the next
    // rename cannot silently hide 11k badges again.
    productBadge: {
      field: 'availability',
      source: 'customerStock',
      defaultBackground: '#6b7280',
      values: {
        'ΜΗΔΕΝΙΚΟ ΑΠΟΘΕΜΑ': { background: '#006b99' },
        'ΥΨΗΛΗ ΔΙΑΘΕΣΙΜΟΤΗΤΑ': { background: '#4f8751' },
        'ΜΕΣΑΙΑ ΔΙΑΘΕΣΙΜΟΤΗΤΑ': { background: '#87ce8f' },
        'ΠΕΡΙΟΡΙΣΜΕΝΗ ΔΙΑΘΕΣΙΜΟΤΗΤΑ': { background: '#c7d37f' },
        'ΠΟΛΥ ΠΕΡΙΟΡΙΣΜΕΝΗ ΔΙΑΘΕΣΙΜΟΤΗΤΑ': { background: '#a0d5ec' },
        'ΕΛΛΕΙΨΗ': { background: '#006b99' },
      },
    },
    // syfak's existing "Αριάδνη" chat-bot avatar (from their landing page) —
    // replaces the sparkle + "OPEN CHAT" button so the widget's assistant
    // matches their site branding. Name shown is aiAssistantTitle ("Αριάδνη").
    chatAvatarUrl: '/clients/syfak/ariadni.png',
    // Label next to the avatar on the entry button — the logo alone isn't
    // self-evidently a chat at 44px.
    chatOpenLabel: 'Ρωτήστε την Αριάδνη',
    // The way BACK from the chat to the search results. Requested by syfak's
    // team 2026-09-10: it read "CLOSE CHAT" — the last English string left in
    // that header — and named the mechanism rather than the outcome. Green so a
    // pharmacist can find it («να χτυπάει στο μάτι»); the tone resolves to THIS
    // client's brandColor (#0a8a5f), and no other client sets chatBackTone, so
    // every other chat header keeps its black button.
    chatBackLabel: 'Επιστροφή στην αναζήτηση',
    chatBackTone: 'brand',
    // Home-screen promo banners (two rectangular slots above popular products).
    // Placeholders for now — set `imageUrl` (e.g. '/clients/syfak/promo-1.png')
    // and optional `linkUrl` once syfak supplies the artwork.
    promoBanners: [
      {
        imageUrl: '/clients/syfak/promo-1.png',
        linkUrl: 'https://syfakb2b.gr/index.php?route=extension/module/product_price_list/list&category_id=201',
        alt: 'ΡΑΦΙ ΠΡΟΣΦΟΡΩΝ',
      },
      {
        imageUrl: '/clients/syfak/promo-2.png',
      },
    ],
    categoriesToHide: new Set<string>([]),
    // PLACEHOLDER: confirm with syfak / replace with real query analytics.
    exampleQueries: [
      { emoji: '💊', text: 'παρακεταμόλη', description: 'Active substance' },
      { emoji: '🧴', text: 'αντηλιακό πρόσωπο', description: 'Sunscreen' },
      { emoji: '💪', text: 'βιταμίνη D', description: 'Supplement' },
      { emoji: '🩹', text: 'επίθεμα τραύματος', description: 'Wound care' },
    ],
    topSearches: ['ΙΦΕΤ', 'Τ 4', 'ΚΑΛΛΥΝΤΙΚΑ', 'ΑΝΤΙΗΛΙΑΚΑ', 'TEST ΕΓΚΥΜΟΣΥΝΗΣ'],
    // These are category/property words. With `product_group` + `properties`
    // made searchable (see indexSettings.searchableAttributes, appended LAST so
    // title/brand matches still rank first), they work as plain text queries:
    //  • ΙΦΕΤ       → matches the `properties` flag value → ~861
    //  • ΚΑΛΛΥΝΤΙΚΑ → matches the ΚΑΛΛΥΝΤΙΚΑ-* product groups → cosmetics
    //  • ΑΝΤΙΗΛΙΑΚΑ → matches the ΚΑΛΛΥΝΤΙΚΑ-ΑΝΤΙΗΛΙΑΚΑ group + the SUN synonym
    // Only Τ 4 needs an override: as text it tokenises to t+4 (noisy); "t4"
    // hits the t4↔levothyroxine synonym for the clean line.
    topSearchActions: {
      'Τ 4': { query: 't4' },
    },
    // Home "popular products" — pinned by SKU (model), shown in THIS order:
    // 60939 PAMPERS · 104388 FREZYDERM serum · 106501 F-HEALTH magnesium · 103423 ORAL-B kids
    popularProductCodes: ['60939', '104388', '106501', '103423'],
    // ── B2B card tailoring (matches syfakb2b.gr's own product card) ──
    productCodeLabel: 'ΚΩΔΙΚΟΣ',
    priceLabel: 'ΤΙΜΗ ΡΟΗΣ',
    offerParticipationLabel: 'ΣΥΜΜΕΤΕΧΕΙ ΣΕ ΠΡΟΣΦΟΡΕΣ',
    alwaysShowActions: true, // B2B/touch: price + action permanently visible, not on hover
    // Live status tags from the pricing endpoint's `labels`, in their own Greek
    // wording (per syfak's team). A '1' flag renders a tag; kontini_partida is a
    // date ("ΗΜ.ΛΗΞΗΣ: …"); message is free text. Order = display order.
    // Tag colours mirror syfak's own site (founder-specified 2026-07-28): red for
    // the two statuses that block a normal order, blue for the ΕΛΛΕΙΠΤΙΚΟ family,
    // grey (the default, so it can be omitted) for the rest.
    pricingLabels: [
      { key: 'kontoliksimo', label: 'ΚΟΝΤΟΛΗΞΙΜΟ' },
      { key: 'kontini_partida', label: 'ΗΜ.ΛΗΞΗΣ', type: 'date' },
      { key: 'fridge', label: 'ΨΥΓΕΙΟ' },
      { key: 'suntagi', label: 'ΕΙΔΟΣ ΜΕ ΣΥΝΤΑΓΗ', tone: 'red' },
      { key: 'drug', label: 'ΝΑΡΚΩΤΙΚΟ' },
      { key: 'hospital', label: 'ΝΟΣΟΚΟΜΕΙΑΚΟ' },
      { key: 'xarak_ifet', label: 'ΧΑΡΑΚΤ. ΙΦΕΤ' },
      { key: 'desmeusi', label: 'ΕΙΔΟΣ ΔΕΣΜΕΥΣΗΣ', tone: 'red' },
      { key: 'eliptiko', label: 'ΕΛΛΕΙΠΤΙΚΟ', tone: 'blue' },
      // Same family as ΕΛΛΕΙΠΤΙΚΟ and shown blue on syfak's own site, so it takes
      // the same tone — confirm with the founder if he wants only the plain one.
      { key: 'eliptika_analogias', label: 'ΕΛΛΕΙΠΤΙΚΟ ΕΚΤΟΣ ΑΝΑΛΟΓΙΑΣ', tone: 'blue' },
      { key: 'stamatimeno', label: 'ΚΑΤΑΡΓΗΜΕΝΟ' },
      { key: 'message', label: '', type: 'text' },
    ],
    strings: {
      // Split into a shared opening + cycling tails: SearchBar holds
      // «Αναζήτηση » static and types only the part that differs, so the common
      // words don't flicker on every cycle and each variant is short enough to
      // fit a phone (the old single sentence truncated at «δραστικής ου…»).
      // These mirror what syfak search actually supports — see §5.9.
      // "|" marks the fixed opening — «Αναζήτηση » is held static and only the
      // tail types/erases. Omit the bar on any entry to type the whole line.
      // Chosen for real coverage in the index, not just what is searchable:
      // model/ΚΩΔΙΚΟΣ 100% · barcode 89% · δραστική ουσία 12%. ΕΟΦ was dropped —
      // only 13% carry one, AND it is not the code printed on our cards (that is
      // `model`), so it pointed pharmacists at the wrong number.
      searchPlaceholder: [
        'Αναζήτηση |προϊόντος',
        'Αναζήτηση |κωδικού',
        'Αναζήτηση |δραστικής ουσίας',
        'Αναζήτηση |barcode',
      ],
      brandLabel: 'Κατασκευαστής',
      filtersTitle: 'Φίλτρα',
      suggestedCategories: 'Προτεινόμενες Κατηγορίες',
      otherCategories: 'Άλλες Κατηγορίες',
      priceRange: 'Εύρος Τιμής',
      productsTitle: 'Προϊόντα',
      noResults: 'Δε βρέθηκαν προϊόντα',
      noResultsHint: 'Δοκιμάστε διαφορετικές λέξεις-κλειδιά ή πιο γενικούς όρους',
      showLess: 'Λιγότερα',
      showMore: (n: number) => `+${n} ακόμα...`,
      popularProducts: 'Δημοφιλή Προϊόντα',
      searchingSuggestions: 'Αναζήτηση Προτάσεων',
      searchingProducts: 'Αναζήτηση προϊόντων...',
      addToCart: 'ΠΡΟΣΘΗΚΗ ΣΤΟ ΚΑΛΑΘΙ',
      topSearchesTitle: 'Top Αναζητήσεις',
      // syfak brands their assistant "Αριάδνη" (paired with chatAvatarUrl).
      aiAssistantTitle: 'Αριάδνη',
      aiAssistantGreeting: 'Πώς μπορώ να βοηθήσω; Ρωτήστε με για σκευάσματα, δραστικές ουσίες, κωδικούς ΕΟΦ ή διαθεσιμότητα.',
    },
  },
};
